import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, output } from '@angular/core';

@Component({
  selector: 'app-ranger-selector',
  imports: [],
  template: `
    <p class="rangos" [class.rangos--below]="labelsPosition() === 'below'">
      <span class="min">{{ minLabelPrefix() }}{{ minLabel() }}</span>
      <span class="max">{{ maxLabelPrefix() }}{{ maxLabel() }}</span>
    </p>
    <input
      type="range"
      class="slider"
      [attr.id]="rangeId()"
      [attr.name]="name() || null"
      [attr.aria-label]="ariaLabel() || null"
      [min]="min()"
      [max]="max()"
      [step]="step()"
      [value]="value()"
      [style.background]="trackBackground()"
      (input)="handleInput($event.target)" />
    @if (showValue()) {
      <div class="margen" [style.margin-left]="bubbleMargin()">
        <p>{{ displayValue() }}</p>
      </div>
    }
  `,
  styleUrl: './ranger-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RangerSelectorComponent {
  readonly rangeId = input('');
  readonly name = input('');
  readonly ariaLabel = input('');
  readonly min = input(0);
  readonly max = input(100);
  readonly step = input(1);
  readonly labelsPosition = input<'above' | 'below'>('above');
  readonly minLabelPrefix = input('');
  readonly maxLabelPrefix = input('');
  readonly showValue = input(true);
  readonly locale = input('en-US');
  // Valor inicial (no controlado luego por el padre); una vez el usuario mueve el slider, el estado vive internamente.
  readonly initialValue = input<number | null>(null);
  // Unidad mostrada junto al valor: '$' formatea como dinero, otros textos (' meses', ' años'...) se anexan tal cual.
  readonly medida = input('');
  // Al cambiar (boton 'Limpiar filtros') el slider vuelve a su valor inicial aunque la vista se reutilice.
  readonly resetKey = input(0);

  readonly valueChange = output<number>();

  private readonly touchedValue = linkedSignal<number, number | null>({
    source: this.resetKey,
    computation: () => null,
  });

  readonly value = computed(() => this.touchedValue() ?? this.initialValue() ?? this.min());

  readonly progressPercent = computed(() => {
    const min = this.min();
    const max = this.max();
    if (max === min) return 0;
    return ((this.value() - min) / (max - min)) * 100;
  });

  readonly trackBackground = computed(() =>
    `linear-gradient(to right, var(--brand-color-tertiary) 0%, var(--brand-color-tertiary) ${this.progressPercent()}%, var(--brand-color-border) ${this.progressPercent()}%, var(--brand-color-border) 100%)`
  );

  readonly bubbleMargin = computed(() => {
    const margin = this.progressPercent() - 20;
    return margin < 0 ? '0%' : `${margin}%`;
  });

  readonly minLabel = computed(() => this.formatValue(this.min(), false));
  readonly maxLabel = computed(() => this.formatValue(this.max(), false));
  readonly displayValue = computed(() => this.formatValue(this.value(), true));

  handleInput(target: EventTarget | null): void {
    const val = parseInt((target as HTMLInputElement).value, 10);
    this.touchedValue.set(val);
    this.valueChange.emit(val);
  }

  private formatValue(val: number, withUnit: boolean): string {
    const medida = this.medida();
    if (medida === '$') {
      return `$${new Intl.NumberFormat(this.locale(), { maximumFractionDigits: 0 }).format(val)}`;
    }
    if (!withUnit) {
      return `${val}`;
    }
    const unit = val === 1 && medida === ' meses' ? ' mes' : medida;
    return `${val}${unit}`;
  }
}
